import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { RESET_PASSWORD } from '../graphql/mutations'
import toast from 'react-hot-toast'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)

  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD, {
    onCompleted: () => {
      toast.success('Password reset! Please sign in with your new password.')
      navigate('/login', { replace: true })
    },
    onError: (error) => {
      setServerError(error.message || 'Invalid or expired password reset link')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setServerError(null)
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    resetPassword({ variables: { token, newPassword: password } })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-seasalt py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <img src="/brand/logo.svg" alt="Curry Me POS" className="h-14 w-auto" />
          </div>
          <h2 className="mt-6 text-center font-primary text-h4 font-bold text-secondary-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-body text-secondary-600">
            Choose a new password for your account
          </p>
        </div>

        {!token ? (
          <div className="text-center space-y-6">
            <p className="text-body text-secondary-700">
              This link is missing its reset token. Please use the link from your email,
              or request a new one.
            </p>
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 font-medium">
              Request a new reset link
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="password" className="sr-only">
                  New password
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="rounded-t-lg"
                  placeholder="New password (min. 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="sr-only">
                  Confirm new password
                </label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="rounded-b-lg"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {serverError && (
              <div className="text-center space-y-2">
                <p className="text-sm text-red-600">{serverError}</p>
                <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  Request a new reset link
                </Link>
              </div>
            )}

            <Button type="submit" isLoading={loading} className="w-full" size="lg">
              Reset password
            </Button>

            <p className="text-center">
              <Link to="/login" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

export default ResetPasswordPage
